import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import {
  BasinType,
  BathroomCompositionDetailDto,
  CompositionRowDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { ProductAttribute } from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import {
  RenderDimensions,
  RenderItem,
  RenderPosition,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-render.models';
import { SelectedElement } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';

@Component({
  selector: 'ng-casto-bathroom-render',
  templateUrl: './bathroom-render.component.html',
  styleUrls: ['./bathroom-render.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BathroomRenderComponent implements OnChanges {
  @Input() composition: BathroomCompositionDetailDto | null = null;
  @Input() selectedElement: SelectedElement | null = null;
  @Input() isLoading = false;

  @Output() elementClick = new EventEmitter<SelectedElement>();

  renderItems: RenderItem[] = [];
  dimensions: RenderDimensions | null = null;

  // Gestion interne du chargement des images
  imagesLoading = false;
  private previousImageUrls: Set<string> = new Set();

  private containerWidth = 300;
  private containerHeight = 300;

  // Décalage global pour gérer les offsets négatifs (shift tout le rendu vers la droite)
  private globalLeftShift = 0;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  /** Retourne true si le loader doit être affiché (chargement en cours OU images en cours de téléchargement) */
  get showLoader(): boolean {
    return this.isLoading || this.imagesLoading;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['composition'] || changes['selectedElement']) {
      this.buildRenderItems();

      // Vérifier si les URLs d'images ont changé pour précharger les nouvelles images
      if (changes['composition']) {
        this.checkAndPreloadImages();
      }
    }
  }

  /**
   * Vérifie si les URLs d'images ont changé et précharge les nouvelles images
   */
  private checkAndPreloadImages(): void {
    const currentUrls = new Set(this.renderItems.map(item => item.imageUrl));

    // Vérifier s'il y a de nouvelles URLs
    const hasNewImages = [...currentUrls].some(url => !this.previousImageUrls.has(url));

    if (hasNewImages && currentUrls.size > 0) {
      this.imagesLoading = true;
      this.cdr.markForCheck();

      // Précharger toutes les images
      const imagePromises = [...currentUrls].map(url => this.preloadImage(url));

      Promise.all(imagePromises)
        .then(() => {
          this.imagesLoading = false;
          this.previousImageUrls = currentUrls;
          this.cdr.markForCheck();
        })
        .catch(() => {
          // En cas d'erreur, on arrête quand même le loader
          this.imagesLoading = false;
          this.previousImageUrls = currentUrls;
          this.cdr.markForCheck();
        });
    } else {
      this.previousImageUrls = currentUrls;
    }
  }

  /**
   * Précharge une image et retourne une promesse
   */
  private preloadImage(src: string): Promise<void> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // On résout même en cas d'erreur pour ne pas bloquer
      img.src = src;
    });
  }

  ngAfterViewInit(): void {
    this.updateDimensions();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDimensions();
  }

  private updateDimensions(): void {
    const element = this.elementRef.nativeElement;
    const parent = element.parentElement;

    if (parent) {
      const rect = parent.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        this.containerWidth = rect.width;
        this.containerHeight = rect.height;
        this.buildRenderItems();
      }
    }
  }

  // Facteur pour calculer la hauteur visible du plan de toilette en perspective
  // On utilise un pourcentage de la profondeur pour simuler l'effet de vue légèrement du dessus
  private readonly COVER_DEPTH_RENDER_FACTOR = 0.225;
  private readonly COVER_MIN_HEIGHT = 7.3; // Hauteur minimum en unités pour que le cover soit visible
  private readonly INTERMEDIATE_COVER_MIN_HEIGHT = 1.3; // Hauteur minimum en unités pour que le cover intermédiaire soit visible

  // Facteur de réduction pour la hauteur du plan vasque (basin)
  private readonly BASIN_HEIGHT_FACTOR = 1.9;

  // Facteur pour calculer la hauteur visible de la poignée en perspective
  private readonly HANDLE_DEPTH_RENDER_FACTOR = 2;
  private readonly HANDLE_MIN_HEIGHT = 3; // Hauteur minimum en unités pour que la poignée soit visible

  private buildRenderItems(): void {
    this.renderItems = [];

    if (!this.composition?.compositionConfiguration?.rows) {
      return;
    }

    // Trier les rows par index pour s'assurer que la ligne 0 est en premier
    const rows = [...this.composition.compositionConfiguration.rows].sort((a, b) => a.index - b.index);
    let maxRightEdge = 0;
    let minLeftEdge = 0;
    let totalHeightForScale = 0; // Hauteur SANS chevauchement pour le calcul du scale

    rows.forEach(row => {
      const rowWidth = row.columns?.reduce((sum, col) => sum + col.width, 0) || row.width || 0;
      // Prendre en compte l'offset pour la largeur totale visible (positif ou négatif)
      const rowOffset = row.index > 0 ? row.offset || 0 : 0;
      minLeftEdge = Math.min(minLeftEdge, rowOffset);
      maxRightEdge = Math.max(maxRightEdge, rowOffset + rowWidth);

      // Hauteurs des éléments avec facteurs de rendu
      const basinRenderHeight = this.getBasinRenderHeight(row);
      // Calcul de la hauteur du cover avec effet de profondeur
      const coverRenderHeight = this.getCoverRenderHeight(row);
      const columnHeight =
        row.columns?.reduce((maxH, col) => {
          return Math.max(maxH, col.product?.heightThickness || 0);
        }, 0) || 30;

      // 1. Colonnes (meubles/tiroirs)
      if (row.columns && row.columns.length > 0) {
        // Trier les colonnes par index
        const sortedColumns = [...row.columns].sort((a, b) => a.index - b.index);
        sortedColumns.forEach(column => {
          if (column.product?.renderImageUrl) {
            this.renderItems.push({
              imageUrl: column.product.renderImageUrl,
              width: column.width,
              height: column.product.heightThickness || 30,
              depth: column.product.depth,
              rowIndex: row.index,
              columnIndex: column.index,
              type: 'column',
              zIndex: 1,
              isInteractive: true,
            });

            // 1b. Poignée à fixer (si présente sur ce tiroir)
            if (column.handle?.renderImageUrl && column.product?.attribute === ProductAttribute.UnderWashbasin) {
              // Calculer la hauteur de rendu de la poignée avec effet de profondeur
              const handleHeight = this.getHandleRenderHeight(column.handle);
              this.renderItems.push({
                imageUrl: column.handle.renderImageUrl,
                width: column.handle.width || 20,
                height: handleHeight,
                depth: column.handle.depth,
                rowIndex: row.index,
                columnIndex: column.index,
                type: 'handle',
                zIndex: 3, // Au-dessus des tiroirs
                isInteractive: true,
              });
            }
          }
        });
      }

      // 2. Plan de toilette (cover)
      if (row.index === 0 && row.coverProduct?.renderImageUrl) {
        this.renderItems.push({
          imageUrl: row.coverProduct.renderImageUrl,
          width: rowWidth,
          height: coverRenderHeight,
          depth: row.coverProduct.depth,
          rowIndex: row.index,
          columnIndex: 0,
          type: 'cover',
          zIndex: 4,
          isInteractive: true,
        });
      }

      // 3. Vasque (Gestion Plan Vasque vs Vasque à poser)
      if (row.index === 0 && row.basinProduct?.renderImageUrl) {
        const isPlanVasque = !row.coverProduct; // Si pas de cover, c'est un Plan Vasque
        const basinType = row.basinType ?? BasinType.Simple;
        const rowWidth = row.columns?.reduce((sum, col) => sum + col.width, 0) || row.width || 0;

        if (isPlanVasque) {
          // CAS PLAN VASQUE : Prend toute la largeur, offset 0
          this.renderItems.push({
            imageUrl: row.basinProduct.renderImageUrl,
            width: rowWidth, // On force la largeur du plan
            height: basinRenderHeight,
            rowIndex: row.index,
            columnIndex: 0,
            type: 'basin',
            zIndex: 5,
            offset: 0, // Toujours 0 pour un plan vasque
            isInteractive: true,
          });
        } else {
          // CAS VASQUE À POSER
          const basinWidth = row.basinProduct.width || 40;

          if (basinType === BasinType.Double) {
            const sortedCols = row.columns ? [...row.columns].sort((a, b) => a.index - b.index) : [];
            // Filtrer les colonnes non-niche pour positionner les vasques uniquement au-dessus des tiroirs
            const nonNicheCols = sortedCols.filter(col => col.product?.attribute !== ProductAttribute.Niche);
            // Priorité : colonnes non-niche ET sans élément fixe (hasElementFix = false)
            const resizableCols = nonNicheCols.filter(col => !col.hasElementFix);
            const targetCols = resizableCols.length >= 2 ? resizableCols : nonNicheCols;

            if (targetCols.length >= 2) {
              // Positionner les 2 vasques sur la première et la dernière colonne cible
              const firstTargetIndex = sortedCols.findIndex(col => col.id === targetCols[0].id);
              const lastTargetIndex = sortedCols.findIndex(col => col.id === targetCols[targetCols.length - 1].id);

              const firstBasinOffset = this.calculateBasinCenterOnColumn(sortedCols, firstTargetIndex, basinWidth);
              const secondBasinOffset = this.calculateBasinCenterOnColumn(sortedCols, lastTargetIndex, basinWidth);

              this.renderItems.push({
                imageUrl: row.basinProduct.renderImageUrl,
                width: basinWidth,
                height: basinRenderHeight,
                rowIndex: row.index,
                columnIndex: 0,
                type: 'basin',
                zIndex: 5,
                offset: firstBasinOffset,
                isInteractive: true,
              });

              this.renderItems.push({
                imageUrl: row.basinProduct.renderImageUrl,
                width: basinWidth,
                height: basinRenderHeight,
                rowIndex: row.index,
                columnIndex: 1,
                type: 'basin',
                zIndex: 5,
                offset: secondBasinOffset,
                isInteractive: true,
              });
            } else {
              // Fallback : si moins de 2 colonnes cibles (ex: un seul grand meuble), on positionne 2 vasques
              let targetWidth = rowWidth;
              let startOffset = 0;

              if (targetCols.length === 1) {
                const col = targetCols[0];
                targetWidth = col.width;
                // Calculer l'offset de début de cette colonne
                for (const c of sortedCols) {
                  if (c.id === col.id) break;
                  startOffset += c.width;
                }
              }

              const firstBasinCenter = startOffset + targetWidth * 0.25;
              const secondBasinCenter = startOffset + targetWidth * 0.75;

              this.renderItems.push({
                imageUrl: row.basinProduct.renderImageUrl,
                width: basinWidth,
                height: basinRenderHeight,
                rowIndex: row.index,
                columnIndex: 0,
                type: 'basin',
                zIndex: 5,
                offset: firstBasinCenter - basinWidth / 2,
                isInteractive: true,
              });

              this.renderItems.push({
                imageUrl: row.basinProduct.renderImageUrl,
                width: basinWidth,
                height: basinRenderHeight,
                rowIndex: row.index,
                columnIndex: 1,
                type: 'basin',
                zIndex: 5,
                offset: secondBasinCenter - basinWidth / 2,
                isInteractive: true,
              });
            }
          } else {
            // Vasque simple à poser
            const basinOffset = this.getBasinOffset(row, basinWidth);
            this.renderItems.push({
              imageUrl: row.basinProduct.renderImageUrl,
              width: basinWidth,
              height: basinRenderHeight,
              rowIndex: row.index,
              columnIndex: 0,
              type: 'basin',
              zIndex: 5,
              offset: basinOffset,
              isInteractive: true,
            });
          }
        }
      }
      // Calculer la hauteur TOTALE sans chevauchement pour le scale
      if (row.index === 0) {
        totalHeightForScale += basinRenderHeight + coverRenderHeight + columnHeight;
      } else {
        totalHeightForScale += columnHeight;
      }
    });

    // 4. Plan de toilette intermédiaire (entre row 0 et row 1)
    // Affiché quand : offset !== 0 OU les deux rangées ont des largeurs différentes
    const firstRow = rows.find(r => r.index === 0);
    const secondRow = rows.find(r => r.index === 1);
    const firstRowWidth = firstRow?.columns?.reduce((sum, col) => sum + col.width, 0) || firstRow?.width || 0;
    const secondRowWidth = secondRow?.columns?.reduce((sum, col) => sum + col.width, 0) || secondRow?.width || 0;
    const hasOffset = (secondRow?.offset || 0) !== 0;
    const hasDifferentWidths = firstRow && secondRow && firstRowWidth !== secondRowWidth;

    if (secondRow && (hasOffset || hasDifferentWidths)) {
      // Utiliser le coverProduct de la deuxième ligne s'il existe
      const coverProduct = secondRow.coverProduct;
      if (coverProduct?.renderImageUrl) {
        const intermediateCoverHeight = this.getCoverRenderHeightFromProduct(coverProduct);

        this.renderItems.push({
          imageUrl: coverProduct.renderImageUrl,
          width: secondRowWidth,
          height: intermediateCoverHeight,
          depth: coverProduct.depth,
          rowIndex: 1, // Associé à la row 1 pour le positionnement
          columnIndex: 0,
          type: 'intermediate-cover',
          zIndex: 0,
          isInteractive: true, // Interactif pour pouvoir modifier la finition
        });

        // Ajouter seulement la tranche visible au total (le reste est caché par le tiroir row 0)
        totalHeightForScale += this.INTERMEDIATE_COVER_MIN_HEIGHT;
      }
    }

    // Calculer la largeur totale visible et le décalage global pour les offsets négatifs
    const maxRowWidth = maxRightEdge - minLeftEdge;
    this.globalLeftShift = -minLeftEdge; // 0 si offset positif, |offset| si offset négatif

    // Marge de sécurité pour éviter le crop
    const padding = 0.9;

    // Largeur de référence (140cm) pour éviter un zoom excessif sur les petits meubles.
    // Si le meuble est plus petit que 140cm, on garde l'échelle du 140cm.
    // Si le meuble est plus grand (ou avec offset), on dézoome pour qu'il rentre.
    const effectiveWidth = Math.max(maxRowWidth, 140);

    this.dimensions = {
      totalWidth: maxRowWidth,
      totalHeight: totalHeightForScale,
      scale: Math.min(
        (this.containerWidth / effectiveWidth) * padding,
        (this.containerHeight / totalHeightForScale) * padding
      ),
    };

    this.cdr.markForCheck();
  }

  private getBasinOffset(row: CompositionRowDto, basinWidth: number): number {
    // Si c'est un plan vasque (pas de cover), l'offset est toujours 0
    if (!row.coverProduct) return 0;

    const sortedCols = [...(row.columns || [])].sort((a, b) => a.index - b.index);

    if (sortedCols.length === 0) {
      const rowWidth = row.width || 0;
      return (rowWidth - basinWidth) / 2;
    }

    // Filtrer les colonnes non-niche
    const nonNicheCols = sortedCols.filter(col => col.product?.attribute !== ProductAttribute.Niche);

    // Si aucune colonne non-niche, centrer sur la largeur totale
    if (nonNicheCols.length === 0) {
      const rowWidth = sortedCols.reduce((sum, col) => sum + col.width, 0);
      return (rowWidth - basinWidth) / 2;
    }

    // Priorité : colonnes non-niche ET sans élément fixe (hasElementFix = false)
    const resizableCols = nonNicheCols.filter(col => !col.hasElementFix);
    const targetCols = resizableCols.length > 0 ? resizableCols : nonNicheCols;

    const middleTargetIndex = Math.floor(targetCols.length / 2);
    const middleTarget = targetCols[middleTargetIndex];
    const targetColumnIndex = sortedCols.findIndex(col => col.id === middleTarget.id);

    return this.calculateBasinCenterOnColumn(sortedCols, targetColumnIndex, basinWidth);
  }

  /**
   * Calcule l'offset pour centrer une vasque au-dessus d'une colonne spécifique
   */
  private calculateBasinCenterOnColumn(
    sortedColumns: { width: number; index: number }[],
    columnIndex: number,
    basinWidth: number
  ): number {
    // Calculer l'offset de début de la colonne cible
    let columnStart = 0;
    for (let i = 0; i < columnIndex; i++) {
      columnStart += sortedColumns[i].width;
    }

    const columnWidth = sortedColumns[columnIndex]?.width || 0;

    // Centrer la vasque sur la colonne
    const columnCenter = columnStart + columnWidth / 2;
    return columnCenter - basinWidth / 2;
  }

  /**
   * Calcule la hauteur de rendu du plan de toilette (cover) pour créer un effet de profondeur.
   * On utilise la profondeur du produit pour simuler une vue légèrement en plongée.
   */
  private getCoverRenderHeight(row: CompositionRowDto): number {
    if (!row.coverProduct) {
      return 0;
    }

    return this.getCoverRenderHeightFromProduct(row.coverProduct);
  }

  /**
   * Calcule la hauteur de rendu d'un plan de toilette à partir d'un produit.
   */
  private getCoverRenderHeightFromProduct(coverProduct: { heightThickness?: number; depth?: number }): number {
    const thickness = coverProduct.heightThickness || 0;
    const depth = coverProduct.depth || 0;

    // Calculer la hauteur visible : épaisseur réelle + portion de la profondeur pour l'effet de perspective
    const depthContribution = depth * this.COVER_DEPTH_RENDER_FACTOR;
    const calculatedHeight = thickness + depthContribution;

    // S'assurer d'une hauteur minimum pour que le cover soit visible
    return Math.max(calculatedHeight, this.COVER_MIN_HEIGHT);
  }

  /**
   * Calcule la hauteur de rendu du plan vasque (basin) avec un facteur de réduction.
   */
  private getBasinRenderHeight(row: CompositionRowDto): number {
    if (!row.basinProduct) {
      return 0;
    }
    const originalHeight = row.basinProduct.heightThickness || 0;

    // Si c'est un plan vasque (pas de cover), on n'applique pas le facteur
    if (!row.coverProduct) {
      return originalHeight;
    }

    return originalHeight * this.BASIN_HEIGHT_FACTOR;
  }

  /**
   * Calcule la hauteur de rendu de la poignée avec effet de profondeur.
   */
  private getHandleRenderHeight(handleProduct: { heightThickness?: number; depth?: number }): number {
    const thickness = handleProduct.heightThickness || 0;
    const depth = handleProduct.depth || 0;

    // Calculer la hauteur visible : épaisseur réelle + portion de la profondeur pour l'effet de perspective
    const depthContribution = depth * this.HANDLE_DEPTH_RENDER_FACTOR;
    const calculatedHeight = thickness + depthContribution;

    // S'assurer d'une hauteur minimum pour que la poignée soit visible
    return Math.max(calculatedHeight, this.HANDLE_MIN_HEIGHT);
  }

  getViewportStyle(): Record<string, string> {
    if (!this.dimensions || !this.composition?.compositionConfiguration) {
      return {};
    }

    const scale = this.dimensions.scale;
    const rows = [...this.composition.compositionConfiguration.rows].sort((a, b) => a.index - b.index);
    const firstRow = rows.find(r => r.index === 0);

    // Facteurs de chevauchement
    const coverOverlapFactor = 0.1;

    // Calculer la hauteur RÉELLE avec chevauchement
    let realHeight = 0;

    if (firstRow) {
      const basinRenderHeight = this.getBasinRenderHeight(firstRow);
      const coverRenderHeight = this.getCoverRenderHeight(firstRow);
      const columnHeight =
        firstRow.columns?.reduce((maxH, col) => {
          return Math.max(maxH, col.product?.heightThickness || 0);
        }, 0) || 30;

      if (coverRenderHeight > 0) {
        // Cas "Vasque à poser" : avec chevauchement du cover sur les tiroirs
        const coverOverlap = coverRenderHeight * coverOverlapFactor;
        realHeight = basinRenderHeight + coverRenderHeight - coverOverlap + columnHeight;
      } else {
        // Cas "Plan vasque" : sans chevauchement entre le plan vasque et les tiroirs
        realHeight = basinRenderHeight + columnHeight;
      }
    }

    // Ajouter les autres lignes si présentes
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const columnHeight =
        row.columns?.reduce((maxH, col) => {
          return Math.max(maxH, col.product?.heightThickness || 0);
        }, 0) || 30;

      // Ajouter seulement la tranche visible du cover intermédiaire (row 1 avec offset ou largeurs différentes)
      if (i === 1 && ((row.offset || 0) !== 0 || row.width !== firstRow?.width) && row?.coverProduct) {
        realHeight += this.INTERMEDIATE_COVER_MIN_HEIGHT;
      }

      realHeight += columnHeight;
    }

    const scaledWidth = this.dimensions.totalWidth * scale;
    const scaledHeight = realHeight * scale;

    return {
      position: 'absolute',
      width: `${scaledWidth}px`,
      height: `${scaledHeight}px`,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  getItemPosition(item: RenderItem): RenderPosition {
    if (!this.dimensions || !this.composition?.compositionConfiguration) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    const scale = this.dimensions.scale;
    const rows = [...this.composition.compositionConfiguration.rows].sort((a, b) => a.index - b.index);
    const currentRow = rows.find(r => r.index === item.rowIndex);

    if (!currentRow) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    // Position horizontale (left)
    let left = 0;

    // Ajouter l'offset de la row pour les rangées autres que la première
    if (item.rowIndex > 0 && currentRow.offset && item.type !== 'intermediate-cover') {
      left = currentRow.offset;
    }

    if (item.type === 'column') {
      const sortedColumns = [...(currentRow.columns || [])].sort((a, b) => a.index - b.index);
      for (let i = 0; i < item.columnIndex; i++) {
        const previousColumn = sortedColumns[i];
        if (previousColumn) {
          left += previousColumn.width;
        }
      }
    } else if (item.type === 'handle') {
      // La poignée est centrée horizontalement sur le tiroir
      const sortedColumns = [...(currentRow.columns || [])].sort((a, b) => a.index - b.index);
      // Calculer la position de début du tiroir
      for (let i = 0; i < item.columnIndex; i++) {
        const previousColumn = sortedColumns[i];
        if (previousColumn) {
          left += previousColumn.width;
        }
      }
      // Centrer la poignée sur le tiroir
      const currentColumn = sortedColumns[item.columnIndex];
      if (currentColumn) {
        const columnWidth = currentColumn.width;
        const handleWidth = item.width;
        left += (columnWidth - handleWidth) / 2;
      }
    } else if (item.type === 'cover') {
      left = 0;
    } else if (item.type === 'intermediate-cover') {
      // Le cover intermédiaire est décalé avec l'offset de la row 1
      const secondRow = rows.find(r => r.index === 1);
      left = secondRow?.offset || 0;
    } else if (item.type === 'basin' && item.offset !== undefined) {
      left = item.offset;
    }

    left = (left + this.globalLeftShift) * scale;

    // Position verticale (top) avec chevauchement
    let top = 0;

    // Facteurs de chevauchement (en pourcentage de la hauteur de l'élément au-dessus)
    const basinOverlapFactor = 0.7; // La vasque chevauche 30% du cover
    const coverOverlapFactor = 0.02; // Le cover chevauche 10% du tiroir

    const secondRow = rows.find(r => r.index === 1);
    const firstRow = rows.find(r => r.index === 0);

    if (item.rowIndex === 0) {
      if (item.type === 'basin') {
        // La vasque descend pour chevaucher le cover (elle n'est plus à top=0)
        const coverRenderHeight = this.getCoverRenderHeight(currentRow);
        if (coverRenderHeight > 0) {
          // Cas "Vasque à poser" : la vasque descend pour chevaucher le cover
          const overlapAmount = coverRenderHeight * basinOverlapFactor;
          top = overlapAmount;
        } else {
          // Cas "Plan vasque" : pas de cover, la vasque reste en haut
          top = 0;
        }
      } else if (item.type === 'cover') {
        // Le cover est positionné après la vasque (sans chevauchement du côté du cover)
        const basinRenderHeight = this.getBasinRenderHeight(currentRow);
        top = basinRenderHeight;
      } else if (item.type === 'column' || item.type === 'handle') {
        // Le tiroir (et la poignée) commence après le cover (si présent), moins le chevauchement du cover
        const basinRenderHeight = this.getBasinRenderHeight(currentRow);
        const coverRenderHeight = this.getCoverRenderHeight(currentRow);

        if (coverRenderHeight > 0) {
          // Cas "Vasque à poser" : le tiroir est sous le cover
          const coverOverlap = coverRenderHeight * coverOverlapFactor;
          top = basinRenderHeight + coverRenderHeight - coverOverlap;
        } else {
          // Cas "Plan vasque" : le tiroir est directement sous le plan vasque, sans chevauchement
          top = basinRenderHeight;
        }
      }
    } else if (item.type === 'intermediate-cover') {
      // Le cover intermédiaire remonte pour être partiellement caché par le tiroir row 0
      // Seule la tranche (épaisseur) doit être visible
      if (firstRow && secondRow?.coverProduct) {
        const basinRenderHeight = this.getBasinRenderHeight(firstRow);
        const coverRenderHeight = this.getCoverRenderHeight(firstRow);
        const columnHeight = Math.max(...(firstRow.columns?.map(col => col.product?.heightThickness || 0) || [0]));
        const intermediateCoverHeight = this.getCoverRenderHeightFromProduct(secondRow.coverProduct);
        const visibleThickness = this.INTERMEDIATE_COVER_MIN_HEIGHT;
        // Le cover remonte de (hauteur - tranche visible) pour être caché par le tiroir
        const overlapAmount = intermediateCoverHeight - visibleThickness;

        if (coverRenderHeight > 0) {
          // Cas "Vasque à poser" : avec chevauchements
          const coverOverlap = coverRenderHeight * coverOverlapFactor;
          // Position = bas du tiroir row 0 - partie à cacher
          top = basinRenderHeight + coverRenderHeight - coverOverlap + columnHeight - overlapAmount;
        } else {
          // Cas "Plan vasque" : sans chevauchement
          top = basinRenderHeight + columnHeight - overlapAmount;
        }
      }
    } else {
      // Pour les autres lignes (colonnes de row 1+)
      for (let i = 0; i < item.rowIndex; i++) {
        const previousRow = rows[i];
        let rowHeight = 0;

        if (i === 0) {
          const basinRenderHeight = this.getBasinRenderHeight(previousRow);
          const coverRenderHeight = this.getCoverRenderHeight(previousRow);
          const columnHeight = Math.max(...(previousRow.columns?.map(col => col.product?.heightThickness || 0) || [0]));

          if (coverRenderHeight > 0) {
            // Cas "Vasque à poser" : avec chevauchements
            const coverOverlap = coverRenderHeight * coverOverlapFactor;
            rowHeight = basinRenderHeight + coverRenderHeight - coverOverlap + columnHeight;
          } else {
            // Cas "Plan vasque" : sans chevauchement
            rowHeight = basinRenderHeight + columnHeight;
          }
        } else {
          const maxColumnHeight = Math.max(
            ...(previousRow.columns?.map(col => col.product?.heightThickness || 0) || [0])
          );
          rowHeight = maxColumnHeight;
        }

        top += rowHeight;
      }

      // Si c'est une colonne ou poignée de row 1+ et qu'il y a un cover intermédiaire, ajouter la tranche visible
      if ((item.type === 'column' || item.type === 'handle') && item.rowIndex === 1) {
        const hasIntermediateOffset = (secondRow?.offset || 0) !== 0;
        const hasIntermediateDifferentWidths = firstRow && secondRow && firstRow.width !== secondRow.width;
        if (secondRow && (hasIntermediateOffset || hasIntermediateDifferentWidths) && secondRow.coverProduct) {
          // Ajouter seulement la tranche visible du cover intermédiaire
          top += this.INTERMEDIATE_COVER_MIN_HEIGHT;
        }
      }
    }

    top = top * scale;

    return {
      left,
      top,
      width: item.width * scale,
      height: item.height * scale,
    };
  }

  getItemStyle(item: RenderItem): Record<string, string | number> {
    const position = this.getItemPosition(item);

    return {
      position: 'absolute',
      left: `${position.left}px`,
      top: `${position.top}px`,
      width: `${position.width}px`,
      height: `${position.height}px`,
      zIndex: item.zIndex,
      transition: 'all 0.3s ease',
      cursor: item.isInteractive ? 'pointer' : 'default',
    };
  }

  getItemClass(item: RenderItem): string {
    const classes = ['render-item', `render-item--${item.type}`];

    if (this.isItemSelected(item)) {
      classes.push('render-item--selected');
    }

    if (item.isInteractive) {
      classes.push('render-item--interactive');
    }

    return classes.join(' ');
  }

  isItemSelected(item: RenderItem): boolean {
    if (!this.selectedElement) return false;

    if (item.type === 'column' && this.selectedElement.type === 'column') {
      return this.selectedElement.rowIndex === item.rowIndex && this.selectedElement.columnIndex === item.columnIndex;
    }

    if (item.type === 'handle' && this.selectedElement.type === 'handle') {
      return this.selectedElement.rowIndex === item.rowIndex && this.selectedElement.columnIndex === item.columnIndex;
    }

    if (item.type === 'cover' && this.selectedElement.type === 'cover') {
      return this.selectedElement.rowIndex === item.rowIndex;
    }

    // Le cover intermédiaire est traité comme un cover de la row 1
    if (item.type === 'intermediate-cover' && this.selectedElement.type === 'cover') {
      return this.selectedElement.rowIndex === item.rowIndex;
    }

    if (item.type === 'basin' && this.selectedElement.type === 'basin') {
      return this.selectedElement.rowIndex === item.rowIndex;
    }

    return false;
  }

  onItemClick(item: RenderItem): void {
    if (!item.isInteractive) return;

    let selectedElement: SelectedElement;

    switch (item.type) {
      case 'column':
        selectedElement = {
          type: 'column',
          rowIndex: item.rowIndex,
          columnIndex: item.columnIndex,
        };
        break;
      case 'handle':
        selectedElement = {
          type: 'handle',
          rowIndex: item.rowIndex,
          columnIndex: item.columnIndex,
        };
        break;
      case 'cover':
      case 'intermediate-cover':
        // Le cover intermédiaire est traité comme un cover de la row 1
        selectedElement = {
          type: 'cover',
          rowIndex: item.rowIndex,
        };
        break;
      case 'basin':
        selectedElement = {
          type: 'basin',
          rowIndex: item.rowIndex,
        };
        break;
      default:
        return;
    }

    this.elementClick.emit(selectedElement);
  }

  trackByItem(index: number, item: RenderItem): string {
    return `${item.type}-${item.rowIndex}-${item.columnIndex}`;
  }

}
